import { createSelector, createSlice } from "@reduxjs/toolkit";
import { type PayloadAction } from "interfaces";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "../../../../enums/reducer";
import { type RequestState, RStatus } from "../../../Application/globaltypes/fetchRequest";
import { type RootState } from "../../../Application/globaltypes/redux";
import { type CustomerPacks, type OwnerPacksLicensing, type PackLicenses } from "../../types/packs";

const namespace = ReducerNamespaceTypes.Reporting;
const entityPrefix = ReducerEntityPrefixTypes.Reporting_Packs;

export interface PackReportingState {
  ownerPacksLicensing: RequestState<OwnerPacksLicensing[]>;
  packLicenses: RequestState<PackLicenses[]>;
  customerPacks: RequestState<CustomerPacks[]>;
}

const initialArrayRequestState: RequestState<any[]> = {
  status: RStatus.Idle,
  value: [],
  errorMessage: undefined,
};

export const initialState: PackReportingState = {
  ownerPacksLicensing: { ...initialArrayRequestState },
  packLicenses: { ...initialArrayRequestState },
  customerPacks: { ...initialArrayRequestState },
};

const packReportingSlice = createSlice({
  name: `${namespace}/${entityPrefix}`,
  initialState,
  reducers: {
    reqOwnerPacksLicensing(state) {
      state.ownerPacksLicensing.status = RStatus.Pending;
    },
    setOwnerPacksLicensing(state, action: PayloadAction<{ data: OwnerPacksLicensing[] }>) {
      state.ownerPacksLicensing = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setOwnerPacksLicensingError(state, action: PayloadAction<{ errorMessage: string }>) {
      state.ownerPacksLicensing.status = RStatus.Error;
      state.ownerPacksLicensing.errorMessage = action.payload.errorMessage;
    },
    reqPackLicenses(state) {
      state.packLicenses.status = RStatus.Pending;
    },
    setPackLicenses(state, action: PayloadAction<{ data: PackLicenses[] }>) {
      state.packLicenses = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setPackLicensesError(state, action: PayloadAction<{ errorMessage: string }>) {
      state.packLicenses.status = RStatus.Error;
      state.packLicenses.errorMessage = action.payload.errorMessage;
    },
    reset() {
      return initialState;
    },
  },
});

export const {
  reqOwnerPacksLicensing,
  setOwnerPacksLicensing,
  setOwnerPacksLicensingError,
  reqPackLicenses,
  setPackLicenses,
  setPackLicensesError,
  reset,
} = packReportingSlice.actions;

// Selectors
export const selectOwnerPacksLicensingState = (state: RootState) => state.reporting.packs.ownerPacksLicensing;
export const selectPackLicensesState = (state: RootState) => state.reporting.packs.packLicenses;
export const selectCustomerPacksState = (state: RootState) => state.reporting.packs.customerPacks;

export const selectOwnerPacksLicensingStatus = createSelector(selectOwnerPacksLicensingState, (state) => state.status);
export const selectOwnerPacksLicensingData = createSelector(selectOwnerPacksLicensingState, (state) => state.value);
export const selectOwnerPacksLicensingError = createSelector(
  selectOwnerPacksLicensingState,
  (state) => state.errorMessage,
);

export const selectPackLicensesStatus = createSelector(selectPackLicensesState, (state) => state.status);
export const selectPackLicensesData = createSelector(selectPackLicensesState, (state) => state.value);
export const selectPackLicensesError = createSelector(selectPackLicensesState, (state) => state.errorMessage);

export default packReportingSlice.reducer;
